"use client";

import { useEffect, useState } from "react";
import { Copy, MapPin, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MarylandOffice() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [countdown, setCountdown] = useState({
    hours: 4,
    minutes: 12,
    seconds: 0
  });
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        if (seconds > 0) {
          seconds -= 1;
        } else if (minutes > 0) {
          minutes -= 1;
          seconds = 59;
        } else if (hours > 0) {
          hours -= 1;
          minutes = 59;
          seconds = 59;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const infoBlocks = [{
    label: "OFFICE",
    lines: ["100 West Road, Suite 400", "Towson, Maryland 21204"]
  }, {
    label: "ACCESS",
    lines: ["I-695 Exit 27A · Ample parking", "Accessible via public transit"]
  }, {
    label: "CONSULTATION",
    lines: ["Mon-Fri · 09:00-17:00", "By appointment only"]
  }];
  const mapSrc = "https://www.openstreetmap.org/export/embed.html?bbox=-76.61%2C39.39%2C-76.59%2C39.41&layer=mapnik&marker=39.40%2C-76.60";
  return <section data-section-id="1782" id="maryland-office" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-3" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(220 20% 4%) 0 6px, hsl(220 20% 8%) 6px 12px)"
    }} aria-hidden="true" />

      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-[1440px]">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-7 relative">
            <div className="relative border border-border h-[380px] md:h-[520px] lg:h-[640px]">
              {}
              <iframe src={mapSrc} className="w-full h-full grayscale contrast-100 brightness-100" style={{
              filter: "grayscale(1) contrast(1) brightness(1)"
            }} title="VESTA Maryland office location" loading="lazy" />

              {}
              <div className="absolute inset-0 pointer-events-none" style={{
              backgroundColor: "hsl(220 20% 4% / 0.1)"
            }} aria-hidden="true" />

              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 md:w-9 md:h-9 border border-foreground" style={{
                  backgroundColor: "hsl(45 100% 48%)"
                }} />
                  <div className="bg-background border border-border px-3 py-2">
                    <span className="font-mono text-[0.625rem] md:text-xs tracking-[0.18em] uppercase text-foreground">
                      <span style={{
                      color: "hsl(45 100% 48%)"
                    }}>■</span> VESTA HQ
                    </span>
                    <div className="font-mono text-[0.625rem] md:text-xs tracking-[0.12em] uppercase text-muted-foreground mt-0.5">
                      TOWSON, MD
                    </div>
                  </div>
                </div>
              </div>

              {}
              <span className="absolute top-3 left-3 w-1.5 h-1.5 md:w-2 md:h-2" style={{
              backgroundColor: "hsl(45 100% 48%)"
            }} aria-hidden="true" />
              <span className="absolute top-3 right-3 w-1.5 h-1.5 md:w-2 md:h-2" style={{
              backgroundColor: "hsl(45 100% 48%)"
            }} aria-hidden="true" />
              <span className="absolute bottom-3 left-3 w-1.5 h-1.5 md:w-2 md:h-2" style={{
              backgroundColor: "hsl(45 100% 48%)"
            }} aria-hidden="true" />
              <span className="absolute bottom-3 right-3 w-1.5 h-1.5 md:w-2 md:h-2" style={{
              backgroundColor: "hsl(45 100% 48%)"
            }} aria-hidden="true" />
            </div>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col">
            {}
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2 h-2" style={{
              backgroundColor: "hsl(45 100% 48%)"
            }} aria-hidden="true" />
              <span className="font-mono text-xs md:text-sm tracking-[0.18em] uppercase text-muted-foreground">
                VOL. 2024 // REF. 09
              </span>
            </div>

            {}
            <h2 className="font-default font-normal uppercase text-foreground leading-[0.92] tracking-tight mb-10" style={{
            fontSize: "clamp(2.5rem, 5.5vw, 5.25rem)",
            fontFamily: "var(--font-serif)"
          }}>
              LOCATE /{" "}
              <span className="block md:inline">
                THE{" "}
                <span className="relative inline-block">
                  FIRM
                  <span className="absolute left-0 right-0 -bottom-1 h-1" style={{
                  backgroundColor: "hsl(45 100% 48% / 0.6)"
                }} aria-hidden="true" />
                </span>
              </span>
            </h2>

            {}
            <div className="flex flex-col">
              {infoBlocks.map((block, i) => <div data-index={i} key={block.label} className="py-5">
                  <div className="font-mono text-xs tracking-[0.18em] uppercase mb-3" style={{
                color: "hsl(45 100% 48%)"
              }}>
                    {block.label}
                  </div>
                  <div className="space-y-1">
                    {block.lines.map((line, j) => <p key={j} className="font-default text-base md:text-lg text-foreground leading-relaxed">
                        {line}
                      </p>)}
                  </div>
                  {}
                  <div className="relative mt-5 h-px w-full" style={{
                backgroundColor: "hsl(220 10% 90% / 0.2)"
              }}>
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5" style={{
                  backgroundColor: "hsl(45 100% 48%)"
                }} aria-hidden="true" />
                    <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5" style={{
                  backgroundColor: "hsl(45 100% 48%)"
                }} aria-hidden="true" />
                  </div>
                </div>)}
            </div>

            {}
            <div className="flex flex-col gap-4 mt-8">
              <Link to="https://www.google.com/maps/search/?api=1&query=100+West+Road+Towson+MD" newTab className="group inline-flex items-center gap-3 font-mono text-sm tracking-[0.12em] uppercase font-semibold w-fit" style={{
              color: "hsl(25 60% 45%)"
            }}>
                <MapPin className="h-5 w-5" strokeWidth={1.5} />
                <span className="underline underline-offset-[6px] decoration-2" style={{
                textDecorationColor: "hsl(25 60% 45%)"
              }}>
                  NAVIGATE TO OFFICE
                </span>
              </Link>
              <Link to="#maryland-office" className="group inline-flex items-center gap-3 font-mono text-sm tracking-[0.12em] uppercase font-semibold w-fit" style={{
              color: "hsl(25 60% 45%)"
            }}>
                <Copy className="h-5 w-5" strokeWidth={1.5} />
                <span className="underline underline-offset-[6px] decoration-2" style={{
                textDecorationColor: "hsl(25 60% 45%)"
              }}>
                  COPY COORDINATES
                </span>
              </Link>
            </div>

            {}
            <div className="mt-10 border border-border bg-card p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Navigation className="h-5 w-5" style={{
                color: "hsl(45 100% 48%)"
              }} strokeWidth={1.5} />
                <span className="font-mono text-[0.6875rem] md:text-xs tracking-[0.18em] uppercase text-muted-foreground">
                  NEXT AVAILABILITY
                </span>
              </div>
              <div className="font-mono text-lg md:text-xl tracking-[0.12em] uppercase font-semibold" style={{
              color: "hsl(45 100% 48%)"
            }}>
                {String(countdown.hours).padStart(2, "0")} HRS{" "}
                {String(countdown.minutes).padStart(2, "0")} MIN{" "}
                {String(countdown.seconds).padStart(2, "0")} SEC
              </div>
            </div>

            {}
            <div className="mt-8">
              <Button asChild className="bg-[hsl(25_60%_45%)] text-white hover:bg-[hsl(220_20%_4%)] font-default font-bold uppercase tracking-[0.08em] px-7 py-4 h-auto text-sm border-none">
                <Link to="/ContactPage">SCHEDULE CONSULTATION</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}